import React, { useState, useMemo } from "react";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { checkAccessPermission } from "../../../utils/utilityFunctions/localServices";
import {
  formatNegativeNumber,
  formatTextValue,
  getMultiTagString,
  getPersonAvatars,
  getTextWithColor,
  getValueDiv,
  safeJsonParse,
} from "../../../utils/utilityFunctions/stringUtility";
import useRequireAuth from "../../../utils/useRequireAuth";
import useDataInputHandlerCopy from "../../../hooks/Table/useTableTbodyFireCopy";
import { useLazyCustomerTableNodeQuery, useLazyFetchPersonInTableQuery } from "../../../utils/customer/customerTable";
import RenderElementsCopy from "../../GlobalFeature/Others/RenderElementsCopy";
import EmailHoverElement from "../../GlobalFeature/Others/EmailHoverElement";
import { renderValue } from "./helperFunctions";

/* -------------------------------------------------------------------------- */
/*                               CONSTANTS                                    */
/* -------------------------------------------------------------------------- */
const TABLE_DETAILS = { tableidclass: "customerotherdetail" };

/* -------------------------------------------------------------------------- */
/*                              MAIN COMPONENT                                */
/* -------------------------------------------------------------------------- */
const CustomRender = ({
  headerData,
  headerBodyData,
  onSubmit,
  groupBySection = true,
  dropdownOptions,
  fetchDropdownApi,
  isCustomModule = false,
}) => {
  const headerBody = headerBodyData[0];

  const [dataInputRow, setDataInputRow] = useState(null);
  const [tablecellInput, setTablecellInput] = useState("");
  const [clientCoord, setClientCoord] = useState({});

  const storedData = safeJsonParse(localStorage.getItem("company_data"));
  const { token } = useRequireAuth();
  const [fetchPersonInTable] = useLazyFetchPersonInTableQuery();

  /* ------------------------------ DATA HANDLER ----------------------------- */
  const handleFetchOptionData = (fieldName) => {
    const field = headerData?.find((f) => f?.key === fieldName);
    if (["dropdown", "multiselect"].includes(field?.type)) fetchDropdownApi({ fieldName });
  };

  const { handleDataInput } = useDataInputHandlerCopy(handleFetchOptionData, setDataInputRow, setClientCoord);

  const fetchPersonData = (data) =>
    fetchPersonInTable({
      token,
      data,
      companyId: storedData?.company_id,
    });

  const updateTableField = (e, item, completeData, header) => {
    const row_id = headerBody?.row_id;
    const column_info = headerData?.find((c) => c?.value === header?.column?.id);
    if (item !== undefined) onSubmit({ fieldValue: item, column_info, row_id });
    setDataInputRow(null);
  };

  const handleOtherDetailChange = (header, item, key, event, type) => {
    event.stopPropagation();
    event.preventDefault();
    if (!dataInputRow?.rowId) handleDataInput(header, item, key, event, type);
  };

  /* ----------------------------- GROUPED HEADERS --------------------------- */
  const groupedHeaders = useMemo(() => {
    if (!groupBySection) return { All: headerData };
    return headerData.reduce((acc, h) => {
      const section = h.sectionName || "General";
      acc[section] = acc[section] || [];
      acc[section].push(h);
      return acc;
    }, {});
  }, [headerData, groupBySection]);

  /* --------------------------- RENDER SECTION ----------------------------- */
  return (
    <div className="flex flex-col gap-4 py-4">
      {Object.entries(groupedHeaders).map(([section, headers]) => (
        <div key={section} className="w-full px-4">
          {groupBySection && <h3 className="text-md font-semibold text-gray-700 mb-2">{section}</h3>}

          <div className="grid grid-cols-2 gap-3">
            {headers
              .filter((fields) => !["task_template", "note_template"].includes(fields?.type))
              .map((header, idx) => {
                const key = header?.key;
                const fieldValue = headerBody?.[key]?.value ?? null;
                const parsedValue = safeJsonParse(fieldValue);
                const editable = checkAccessPermission("Add & Edit Customer");
                const isInputOpen = dataInputRow && dataInputRow?.rowId === header?.label;

                return (
                  <div key={idx} className="h-[40px] mb-6 md:mb-0 relative flex items-center border rounded-md">
                    {/* Label Section */}
                    <div className="bg-[#F1F6F9] w-[40%] h-full flex items-center justify-between p-4 rounded-tl-lg rounded-bl-lg">
                      <label className="tracking-wide text-535252 text-[13px] capitalize">{header?.label}</label>
                      <button
                        type="button"
                        className="pencilicon ms-5"
                        disabled={!editable || Boolean(dataInputRow)}
                        style={{ visibility: editable ? "visible" : "hidden" }}
                      >
                        <i className="bi bi-pencil" />
                      </button>
                    </div>

                    {/* Value Section */}
                    <div className="col-span-7 relative bg-white w-[60%] h-full text-ellipsis px-1 ps-4">
                      {isInputOpen ? (
                        <RenderElementsCopy
                          tableDetail={TABLE_DETAILS}
                          fetchPersonInTable={fetchPersonData}
                          Item={headerBody ?? {}}
                          Header={{ ...header }}
                          RowKey={parsedValue?.field_label}
                          CommonData={{ data: dropdownOptions }}
                          CellChange={(e) => setTablecellInput(e.target.value)}
                          CellInput={tablecellInput}
                          setDataRow={setDataInputRow}
                          DataRow={dataInputRow}
                          UpdateField={updateTableField}
                          clientCoord={clientCoord}
                          cell={{ column: { id: header.key } }}
                          isTracking
                          // isCustomModule
                          meta={header?.meta}
                          isCustomModule={isCustomModule}
                        />
                      ) : (
                        <div
                          className="clickopennow cursor-pointer labelhoverpencilshow accounting-ellipsis flex items-center justify-between h-full"
                          onClick={(e) =>
                            handleOtherDetailChange(header.label, headerBody, header?.key, e, header.type)
                          }
                        >
                          <div>
                            <button type="button" className="pencilicon mx-1 mr-2">
                              <i className="bi bi-pencil" />
                            </button>
                          </div>

                          {renderValue(header, parsedValue, fieldValue)}

                          {fieldValue && header?.type === "person" && (
                            <EmailHoverElement
                              ParentId={TABLE_DETAILS}
                              Data={parsedValue}
                              HoverActiveClass="tablehoverelement"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default CustomRender;
